import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3200';

function authHeaders() {
  const token = JSON.parse(localStorage.getItem('urs-auth') ?? '{}')?.state?.token ?? '';
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function fmtUGX(n: number | string | undefined | null) {
  const num = typeof n === 'string' ? parseInt(n, 10) : Number(n ?? 0);
  if (isNaN(num)) return 'UGX 0';
  return 'UGX ' + Math.round(num).toLocaleString('en-UG');
}

function fmtPeriod(month: number, year: number) {
  return new Date(year, month - 1, 1).toLocaleDateString('en-UG', { month: 'long', year: 'numeric' });
}

function fmtDate(s: string | null | undefined) {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-UG', { day: 'numeric', month: 'short', year: 'numeric' });
}

// API returns lowercase status: draft, approved, paid, rejected
interface PayrollRun {
  id: string;
  periodMonth: number;
  periodYear: number;
  status: string;
  totalGross: number;
  totalNet: number;
  totalPaye: number;
  totalNssfEmployee: number;
  totalNssfEmployer: number;
  totalLst: number;
  _count: { payslips: number };
  createdAt: string;
  paidAt?: string;
}

interface Payslip {
  id: string;
  employee?: { fullName: string; email?: string };
  grossPay: number;
  netPay: number;
  payeDeduction: number;
  nssfEmployee: number;
  nssfEmployer: number;
  lstDeduction: number;
  basicSalary: number;
}

interface SalaryAdvance {
  id: string;
  employee?: { fullName: string };
  requestedAmount: number;
  approvedAmount?: number;
  status: string;
  createdAt: string;
  reason?: string;
}

// ── Static reference data ──────────────────────────────────────────────────

const PAYE_BANDS = [
  { range: 'Up to UGX 235,000', rate: '0%', note: 'Tax-free band' },
  { range: 'UGX 235,001 – 335,000', rate: '10%', base: 'UGX 0', note: '' },
  { range: 'UGX 335,001 – 410,000', rate: '20%', base: 'UGX 10,000', note: '' },
  { range: 'UGX 410,001 – 10,000,000', rate: '30%', base: 'UGX 25,000', note: '' },
  { range: 'Above UGX 10,000,000', rate: '40%', base: 'UGX 2,870,000', note: 'Top rate' },
];

// Correct 10-band table per Local Government (Amendment) No. 2 Act 2008
// Source: docs/uganda_lst_reference.md
const LST_BANDS = [
  { range: 'Up to UGX 100,000/month', annual: 'UGX 0', monthly: 'UGX 0', exempt: true },
  { range: 'UGX 100,001 – 200,000/month', annual: 'UGX 5,000', monthly: 'UGX 417', exempt: false },
  { range: 'UGX 200,001 – 300,000/month', annual: 'UGX 10,000', monthly: 'UGX 833', exempt: false },
  { range: 'UGX 300,001 – 400,000/month', annual: 'UGX 20,000', monthly: 'UGX 1,667', exempt: false },
  { range: 'UGX 400,001 – 500,000/month', annual: 'UGX 30,000', monthly: 'UGX 2,500', exempt: false },
  { range: 'UGX 500,001 – 600,000/month', annual: 'UGX 40,000', monthly: 'UGX 3,333', exempt: false },
  { range: 'UGX 600,001 – 700,000/month', annual: 'UGX 60,000', monthly: 'UGX 5,000', exempt: false },
  { range: 'UGX 700,001 – 800,000/month', annual: 'UGX 70,000', monthly: 'UGX 5,833', exempt: false },
  { range: 'UGX 800,001 – 900,000/month', annual: 'UGX 80,000', monthly: 'UGX 6,667', exempt: false },
  { range: 'UGX 900,001 – 1,000,000/month', annual: 'UGX 90,000', monthly: 'UGX 7,500', exempt: false },
  { range: 'Above UGX 1,000,000/month', annual: 'UGX 100,000', monthly: 'UGX 8,333', exempt: false },
];

// ── Helper components ──────────────────────────────────────────────────────

function Skeleton({ h = 56 }: { h?: number }) {
  return (
    <div style={{
      height: h, borderRadius: 8,
      background: 'var(--surface2)', animation: 'pulse 1.5s infinite',
    }} />
  );
}

function runStatusBadge(s: string) {
  if (s === 'draft') return 'badge-amber';
  if (s === 'approved') return 'badge-blue';
  if (s === 'paid') return 'badge-green';
  if (s === 'rejected') return 'badge-red';
  return 'badge-gray';
}

function advStatusBadge(s: string) {
  if (s === 'pending') return 'badge-amber';
  if (s === 'approved') return 'badge-green';
  if (s === 'rejected') return 'badge-red';
  return 'badge-gray';
}

// Authenticated blob download — avoids UNAUTHENTICATED error when opening export URLs directly
async function downloadBlob(url: string, filename: string) {
  const token = JSON.parse(localStorage.getItem('urs-auth') ?? '{}')?.state?.token ?? '';
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error('Export failed');
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(href);
}

function DownloadButton({ label, url, filename, accent }: { label: string; url: string; filename: string; accent?: string }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleClick = async () => {
    setLoading(true);
    setError('');
    try {
      await downloadBlob(url, filename);
    } catch {
      setError('Download failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
      <button
        className="btn btn-ghost btn-sm"
        style={{ color: accent, opacity: loading ? 0.6 : 1 }}
        onClick={handleClick}
        disabled={loading}
      >
        {loading ? '…' : label}
      </button>
      {error && <span style={{ fontSize: 10, color: 'var(--red)' }}>{error}</span>}
    </div>
  );
}

// ── Modals ─────────────────────────────────────────────────────────────────

function PayslipsModal({ run, onClose }: { run: PayrollRun; onClose: () => void }) {
  const { data: payslips = [], isLoading } = useQuery<Payslip[]>({
    queryKey: ['payslips', run.id],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/payroll/runs/${run.id}/payslips`, { headers: authHeaders() });
      const j = await res.json();
      return j.data ?? [];
    },
  });

  const period = `${run.periodYear}-${String(run.periodMonth).padStart(2, '0')}`;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 860 }}>
        <div className="modal-header">
          <div>
            <h2 className="modal-title">Payslips — {fmtPeriod(run.periodMonth, run.periodYear)}</h2>
            <p style={{ color: 'var(--text-3)', fontSize: 12, marginTop: 2 }}>{run._count.payslips} employees · {run.status.toUpperCase()}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <DownloadButton
              label="Bank CSV"
              url={`${API_BASE}/v1/payroll/runs/${run.id}/export/bank-csv`}
              filename={`bank-transfer-${period}.csv`}
            />
            <DownloadButton
              label="Excel Report"
              url={`${API_BASE}/v1/payroll/runs/${run.id}/export/excel`}
              filename={`payroll-${period}.xlsx`}
              accent="var(--green)"
            />
            <DownloadButton
              label="Payslips PDF"
              url={`${API_BASE}/v1/payroll/runs/${run.id}/export/payslips-pdf`}
              filename={`payslips-${period}.pdf`}
              accent="var(--accent)"
            />
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {isLoading ? (
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} />)}
            </div>
          ) : payslips.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No payslips found.</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--surface)' }}>
                  <tr>
                    <th>Employee</th>
                    <th style={{ textAlign: 'right' }}>Gross</th>
                    <th style={{ textAlign: 'right' }}>PAYE</th>
                    <th style={{ textAlign: 'right' }}>NSSF (Emp)</th>
                    <th style={{ textAlign: 'right' }}>LST</th>
                    <th style={{ textAlign: 'right' }}>Net Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {payslips.map(ps => (
                    <tr key={ps.id}>
                      <td>
                        <p style={{ fontWeight: 500, color: 'var(--text)' }}>{ps.employee?.fullName ?? '—'}</p>
                        {ps.employee?.email && <p style={{ fontSize: 11, color: 'var(--text-3)' }}>{ps.employee.email}</p>}
                      </td>
                      <td style={{ textAlign: 'right', color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(ps.grossPay)}</td>
                      <td style={{ textAlign: 'right', color: 'var(--red)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(ps.payeDeduction)}</td>
                      <td style={{ textAlign: 'right', color: 'var(--amber)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(ps.nssfEmployee)}</td>
                      <td style={{ textAlign: 'right', color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(ps.lstDeduction)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(ps.netPay)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--surface2)' }}>
                    <td style={{ fontWeight: 600, color: 'var(--text-2)', padding: '10px 16px' }}>Totals</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', padding: '10px 16px' }}>{fmtUGX(run.totalGross)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--red)', fontFamily: 'var(--font-mono)', padding: '10px 16px' }}>{fmtUGX(run.totalPaye)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--amber)', fontFamily: 'var(--font-mono)', padding: '10px 16px' }}>{fmtUGX(run.totalNssfEmployee)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', padding: '10px 16px' }}>{fmtUGX(run.totalLst)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--text)', fontFamily: 'var(--font-mono)', padding: '10px 16px' }}>{fmtUGX(run.totalNet)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NewRunModal({ onClose, onSave, isSaving }: { onClose: () => void; onSave: (data: object) => void; isSaving: boolean }) {
  const today = new Date();
  const [periodMonth, setPeriodMonth] = useState(today.getMonth() + 1);
  const [periodYear, setPeriodYear] = useState(today.getFullYear());

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h2 className="modal-title">New Payroll Run</h2>
          <button className="modal-close" onClick={onClose} disabled={isSaving}>&times;</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="field">
              <label className="field-label">Month</label>
              <select className="input" value={periodMonth} onChange={e => setPeriodMonth(Number(e.target.value))}>
                {months.map((m, i) => (
                  <option key={m} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label className="field-label">Year</label>
              <select className="input" value={periodYear} onChange={e => setPeriodYear(Number(e.target.value))}>
                {[2024, 2025, 2026, 2027].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-3)', margin: 0 }}>
            This will generate payslips for all active employees with a salary structure.
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onSave({ periodMonth, periodYear })} disabled={isSaving}>
            {isSaving ? 'Generating…' : 'Generate Run'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DisburseModal({ run, onClose, onSave, isSaving }: { run: PayrollRun; onClose: () => void; onSave: (method: string) => void; isSaving: boolean }) {
  const [method, setMethod] = useState('cash');
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <h2 className="modal-title">Disburse Payroll</h2>
          <button className="modal-close" onClick={onClose} disabled={isSaving}>&times;</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ fontSize: 13, color: 'var(--text-2)', margin: 0 }}>
            Disburse <strong>{fmtPeriod(run.periodMonth, run.periodYear)}</strong> payroll — {run._count.payslips} employees, {fmtUGX(run.totalNet)} net.
          </p>
          <div className="field">
            <label className="field-label">Payment Method</label>
            <select className="input" value={method} onChange={e => setMethod(e.target.value)}>
              <option value="cash">Cash</option>
              <option value="bank">Bank Transfer</option>
              <option value="momo">MTN / Airtel MoMo</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" style={{ background: 'var(--green)', borderColor: 'var(--green)' }} onClick={() => onSave(method)} disabled={isSaving}>
            {isSaving ? 'Disbursing…' : 'Confirm Disburse'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ title, message, confirmLabel, danger, onClose, onConfirm, isSaving }: {
  title: string; message: string; confirmLabel: string; danger?: boolean;
  onClose: () => void; onConfirm: () => void; isSaving: boolean;
}) {
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose} disabled={isSaving}>&times;</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13, color: 'var(--text-2)', margin: 0 }}>{message}</p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button
            className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
            onClick={onConfirm}
            disabled={isSaving}
          >
            {isSaving ? 'Please wait…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tab: PAYE Reference ────────────────────────────────────────────────────

function PayeTab() {
  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>PAYE Tax Bands — 2025 / 2026</h3>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--text-3)' }}>
          Monthly taxable income bands. Source: Uganda Revenue Authority. Updated annually with the national budget.
        </p>
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Monthly Taxable Income</th>
                <th style={{ textAlign: 'center' }}>Rate</th>
                <th style={{ textAlign: 'right' }}>Base Amount</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {PAYE_BANDS.map((band, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 500, color: 'var(--text)' }}>{band.range}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`badge ${band.rate === '0%' ? 'badge-green' : band.rate === '40%' ? 'badge-red' : 'badge-blue'}`}>
                      {band.rate}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--text-2)' }}>{band.base ?? '—'}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-3)' }}>{band.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 10 }}>
        Formula: PAYE = Base Amount + (Income − Band Floor) × Rate. All calculations use monthly gross pay.
      </p>
    </div>
  );
}

// ── Tab: LST Reference ─────────────────────────────────────────────────────

function LstTab() {
  const { data: settings } = useQuery<{ lstMode: string; lstLumpSumMonth: number }>({
    queryKey: ['tenant-settings'],
    queryFn: async () => {
      const token = JSON.parse(localStorage.getItem('urs-auth') ?? '{}')?.state?.token ?? '';
      const res = await fetch(`${API_BASE}/v1/admin/settings`, { headers: { Authorization: `Bearer ${token}` } });
      const j = await res.json();
      return j.data;
    },
  });

  const MONTH_NAMES = ['', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
            Local Service Tax (LST) — Salaried Employees
          </h3>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--text-3)' }}>
            Governed by Local Government (Amendment) No. 2 Act 2008. LST is deducted <strong>before</strong> PAYE is computed.
            Employees earning ≤ UGX 100,000/month are exempt.
          </p>
        </div>
        {settings && (
          <div className="card" style={{ padding: '12px 16px', minWidth: 240, flexShrink: 0 }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', marginBottom: 8 }}>Current Configuration</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ fontSize: 12, color: 'var(--text-2)' }}>Collection Mode</span>
                <span className={`badge ${settings.lstMode === 'lump_sum' ? 'badge-blue' : 'badge-amber'}`}>
                  {settings.lstMode === 'lump_sum' ? 'Lump Sum' : 'Monthly'}
                </span>
              </div>
              {settings.lstMode === 'lump_sum' && (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ fontSize: 12, color: 'var(--text-2)' }}>Collection Month</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)' }}>
                    {MONTH_NAMES[settings.lstLumpSumMonth]}
                  </span>
                </div>
              )}
            </div>
            <p style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 10 }}>
              Change in Settings → Uganda Compliance
            </p>
          </div>
        )}
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Monthly Gross Income</th>
                <th style={{ textAlign: 'right' }}>Annual LST</th>
                <th style={{ textAlign: 'right' }}>Monthly Equivalent</th>
                <th style={{ textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {LST_BANDS.map((band, i) => (
                <tr key={i} style={{ opacity: band.exempt ? 0.6 : 1 }}>
                  <td style={{ fontWeight: 500, color: 'var(--text)' }}>{band.range}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: band.exempt ? 'var(--text-3)' : 'var(--text-2)' }}>{band.annual}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: band.exempt ? 'var(--text-3)' : 'var(--text)' }}>{band.monthly}</td>
                  <td style={{ textAlign: 'center' }}>
                    {band.exempt
                      ? <span className="badge badge-green">Exempt</span>
                      : <span className="badge badge-blue">Liable</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 10 }}>
        Remittance deadline: 15 November. Late payment attracts a 50% surcharge.
        PAYE taxable income = Gross Pay − LST − NSSF Employee (5%).
        Source: Local Government (Amendment) No. 2 Act 2008.
      </p>
    </div>
  );
}

// ── Tab: Earnings (informational) ──────────────────────────────────────────

const EARNINGS_TYPES = [
  { name: 'Basic Salary', field: 'basicSalary', taxable: true, description: 'Core monthly salary set on salary structure' },
  { name: 'Housing Allowance', field: 'housingAllowance', taxable: true, description: 'Monthly housing benefit' },
  { name: 'Transport Allowance', field: 'transportAllowance', taxable: true, description: 'Monthly transport benefit' },
  { name: 'Other Allowance', field: 'otherAllowance', taxable: true, description: 'Miscellaneous allowances' },
  { name: 'Overtime Pay', field: 'overtimePay', taxable: true, description: 'Calculated from overtime hours logged in attendance' },
];

function EarningsTab() {
  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Earning Types</h3>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--text-3)' }}>
          Earning components included in every payroll run. All earnings are taxable and form part of the gross pay.
          Edit allowance amounts in the employee salary structure (HR → Employees → Edit).
        </p>
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Earning Type</th>
                <th>Field</th>
                <th style={{ textAlign: 'center' }}>Taxable</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {EARNINGS_TYPES.map(e => (
                <tr key={e.field}>
                  <td style={{ fontWeight: 500, color: 'var(--text)' }}>{e.name}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-3)' }}>{e.field}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`badge ${e.taxable ? 'badge-amber' : 'badge-green'}`}>{e.taxable ? 'Yes' : 'No'}</span>
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--text-2)' }}>{e.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Tab: Deductions (informational) ───────────────────────────────────────

const DEDUCTION_TYPES = [
  { name: 'PAYE', field: 'payeDeduction', statutory: true, description: 'Pay As You Earn income tax (URA). Calculated from PAYE bands above.' },
  { name: 'NSSF (Employee)', field: 'nssfEmployee', statutory: true, description: '5% of gross pay. Employee contribution to NSSF.' },
  { name: 'NSSF (Employer)', field: 'nssfEmployer', statutory: true, description: '10% of gross pay. Employer contribution — not deducted from net pay.' },
  { name: 'LST', field: 'lstDeduction', statutory: true, description: 'Local Service Tax. See LST tab for rates.' },
  { name: 'Salary Advance Repayment', field: 'salaryAdvanceDeduction', statutory: false, description: 'Monthly installment for approved salary advances (see Salary Advances tab).' },
];

function DeductionsTab() {
  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Deduction Types</h3>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--text-3)' }}>
          Deductions applied automatically in each payroll run. Statutory deductions are required by law.
        </p>
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Deduction Type</th>
                <th>Field</th>
                <th style={{ textAlign: 'center' }}>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {DEDUCTION_TYPES.map(d => (
                <tr key={d.field}>
                  <td style={{ fontWeight: 500, color: 'var(--text)' }}>{d.name}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-3)' }}>{d.field}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`badge ${d.statutory ? 'badge-red' : 'badge-blue'}`}>{d.statutory ? 'Statutory' : 'Voluntary'}</span>
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--text-2)' }}>{d.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

type Tab = 'runs' | 'advances' | 'paye' | 'lst' | 'earnings' | 'deductions';

const TABS: { key: Tab; label: string }[] = [
  { key: 'runs', label: 'Payroll Runs' },
  { key: 'advances', label: 'Salary Advances' },
  { key: 'paye', label: 'PAYE Bands' },
  { key: 'lst', label: 'LST Rates' },
  { key: 'earnings', label: 'Earnings' },
  { key: 'deductions', label: 'Deductions' },
];

export function PayrollPage() {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>('runs');
  const [viewingRun, setViewingRun] = useState<PayrollRun | null>(null);
  const [showNewRun, setShowNewRun] = useState(false);
  const [disbursingRun, setDisbursingRun] = useState<PayrollRun | null>(null);
  const [approvingRun, setApprovingRun] = useState<PayrollRun | null>(null);
  const [deletingRun, setDeletingRun] = useState<PayrollRun | null>(null);
  const [reversingRun, setReversingRun] = useState<PayrollRun | null>(null);

  const { data: runs = [], isLoading: runsLoading } = useQuery<PayrollRun[]>({
    queryKey: ['payroll-runs'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/payroll/runs`, { headers: authHeaders() });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed');
      return j.data ?? [];
    },
  });

  const { data: advances = [], isLoading: advancesLoading } = useQuery<SalaryAdvance[]>({
    queryKey: ['salary-advances'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/payroll/advances`, { headers: authHeaders() });
      const j = await res.json();
      return j.data ?? [];
    },
  });

  const createRun = useMutation({
    mutationFn: async (data: object) => {
      const res = await fetch(`${API_BASE}/v1/payroll/runs`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed');
      return j.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['payroll-runs'] }); setShowNewRun(false); },
  });

  const approveRun = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE}/v1/payroll/runs/${id}/approve`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ action: 'approve' }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed');
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['payroll-runs'] }); setApprovingRun(null); },
  });

  const disburseRun = useMutation({
    mutationFn: async ({ id, paymentMethod }: { id: string; paymentMethod: string }) => {
      const res = await fetch(`${API_BASE}/v1/payroll/runs/${id}/disburse`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ paymentMethod }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed');
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['payroll-runs'] }); setDisbursingRun(null); },
  });

  const deleteRun = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE}/v1/payroll/runs/${id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed');
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['payroll-runs'] }); setDeletingRun(null); },
  });

  const reverseRun = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE}/v1/payroll/runs/${id}/reverse`, {
        method: 'POST', headers: authHeaders(),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed');
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['payroll-runs'] }); setReversingRun(null); },
  });

  const approveAdvance = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'approve' | 'reject' }) => {
      const res = await fetch(`${API_BASE}/v1/payroll/advances/${id}/approve`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ action }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['salary-advances'] }),
  });

  const latestRun = runs[0];
  const pendingAdvances = advances.filter(a => a.status === 'pending').length;

  return (
    <div>
      <div className="page-header">
        <div>
          <p style={{ color: 'var(--text-3)', fontSize: 13 }}>
            {runs.length} payroll runs · {pendingAdvances} advance requests pending
          </p>
        </div>
        {activeTab === 'runs' && (
          <button className="btn btn-primary" onClick={() => setShowNewRun(true)}>+ New Run</button>
        )}
      </div>

      {latestRun && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
          {[
            { label: 'Gross Pay', value: fmtUGX(latestRun.totalGross), color: 'var(--text)' },
            { label: 'Total PAYE', value: fmtUGX(latestRun.totalPaye), color: 'var(--red)' },
            { label: 'Total NSSF', value: fmtUGX(latestRun.totalNssfEmployee), color: 'var(--amber)' },
            { label: 'Net Pay', value: fmtUGX(latestRun.totalNet), color: 'var(--green)' },
          ].map(kpi => (
            <div key={kpi.label} className="stat-card">
              <p className="stat-label">{kpi.label}</p>
              <p className="stat-value" style={{ color: kpi.color, fontFamily: 'var(--font-mono)' }}>{kpi.value}</p>
              <p className="stat-sub">{fmtPeriod(latestRun.periodMonth, latestRun.periodYear)}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, background: 'var(--surface2)', borderRadius: 10, padding: 4, width: 'fit-content', marginBottom: 20, flexWrap: 'wrap' }}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '6px 16px', borderRadius: 8, fontSize: 13, fontWeight: activeTab === tab.key ? 600 : 400,
              background: activeTab === tab.key ? 'var(--surface)' : 'transparent',
              color: activeTab === tab.key ? 'var(--text)' : 'var(--text-3)',
              border: 'none', cursor: 'pointer',
              boxShadow: activeTab === tab.key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.12s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'runs' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {runsLoading ? (
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} />)}
            </div>
          ) : runs.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No payroll runs yet. Generate your first run.</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Period</th>
                    <th style={{ textAlign: 'center' }}>Employees</th>
                    <th style={{ textAlign: 'right' }}>Gross</th>
                    <th style={{ textAlign: 'right' }}>Net Pay</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {runs.map(run => (
                    <tr key={run.id}>
                      <td>
                        <p style={{ fontWeight: 500, color: 'var(--text)' }}>{fmtPeriod(run.periodMonth, run.periodYear)}</p>
                        <p style={{ fontSize: 11, color: 'var(--text-3)' }}>Created {fmtDate(run.createdAt)}</p>
                      </td>
                      <td style={{ textAlign: 'center', color: 'var(--text-2)' }}>{run._count.payslips}</td>
                      <td style={{ textAlign: 'right', color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(run.totalGross)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(run.totalNet)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${runStatusBadge(run.status)}`} style={{ textTransform: 'capitalize' }}>{run.status}</span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => setViewingRun(run)}>View</button>
                          {run.status === 'draft' && (
                            <>
                              <button
                                className="btn btn-ghost btn-sm"
                                style={{ color: 'var(--accent)' }}
                                onClick={() => setApprovingRun(run)}
                              >
                                Approve
                              </button>
                              <button
                                className="btn btn-ghost btn-sm"
                                style={{ color: 'var(--red)' }}
                                onClick={() => setDeletingRun(run)}
                              >
                                Delete
                              </button>
                            </>
                          )}
                          {run.status === 'approved' && (
                            <>
                              <button
                                className="btn btn-ghost btn-sm"
                                style={{ color: 'var(--green)' }}
                                onClick={() => setDisbursingRun(run)}
                              >
                                Disburse
                              </button>
                              <button
                                className="btn btn-ghost btn-sm"
                                style={{ color: 'var(--amber)' }}
                                onClick={() => setReversingRun(run)}
                              >
                                Reverse
                              </button>
                            </>
                          )}
                          {run.status === 'paid' && (
                            <button
                              className="btn btn-ghost btn-sm"
                              style={{ color: 'var(--amber)' }}
                              onClick={() => setReversingRun(run)}
                            >
                              Reverse
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'advances' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {advancesLoading ? (
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h={48} />)}
            </div>
          ) : advances.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No salary advance requests.</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th style={{ textAlign: 'right' }}>Requested</th>
                    <th style={{ textAlign: 'right' }}>Approved</th>
                    <th>Reason</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {advances.map(adv => (
                    <tr key={adv.id}>
                      <td>
                        <p style={{ fontWeight: 500, color: 'var(--text)' }}>{adv.employee?.fullName ?? '—'}</p>
                        <p style={{ fontSize: 11, color: 'var(--text-3)' }}>{fmtDate(adv.createdAt)}</p>
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(adv.requestedAmount)}</td>
                      <td style={{ textAlign: 'right', color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{adv.approvedAmount != null ? fmtUGX(adv.approvedAmount) : '—'}</td>
                      <td style={{ color: 'var(--text-2)' }}>{adv.reason ?? '—'}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${advStatusBadge(adv.status)}`} style={{ textTransform: 'capitalize' }}>{adv.status}</span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {adv.status === 'pending' && (
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--green)' }} onClick={() => approveAdvance.mutate({ id: adv.id, action: 'approve' })}>Approve</button>
                            <button className="btn btn-danger btn-sm" onClick={() => approveAdvance.mutate({ id: adv.id, action: 'reject' })}>Reject</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'paye' && <PayeTab />}
      {activeTab === 'lst' && <LstTab />}
      {activeTab === 'earnings' && <EarningsTab />}
      {activeTab === 'deductions' && <DeductionsTab />}

      {/* Modals */}
      {viewingRun && <PayslipsModal run={viewingRun} onClose={() => setViewingRun(null)} />}

      {showNewRun && (
        <NewRunModal
          onClose={() => setShowNewRun(false)}
          onSave={data => createRun.mutate(data)}
          isSaving={createRun.isPending}
        />
      )}

      {approvingRun && (
        <ConfirmModal
          title="Approve Payroll Run"
          message={`Approve the ${fmtPeriod(approvingRun.periodMonth, approvingRun.periodYear)} payroll run for ${approvingRun._count.payslips} employees? This will lock the payslips for disbursement.`}
          confirmLabel="Approve"
          onClose={() => setApprovingRun(null)}
          onConfirm={() => approveRun.mutate(approvingRun.id)}
          isSaving={approveRun.isPending}
        />
      )}

      {disbursingRun && (
        <DisburseModal
          run={disbursingRun}
          onClose={() => setDisbursingRun(null)}
          onSave={method => disburseRun.mutate({ id: disbursingRun.id, paymentMethod: method })}
          isSaving={disburseRun.isPending}
        />
      )}

      {deletingRun && (
        <ConfirmModal
          title="Delete Payroll Run"
          message={`Delete the ${fmtPeriod(deletingRun.periodMonth, deletingRun.periodYear)} draft payroll run? This cannot be undone. All payslips in this run will be removed.`}
          confirmLabel="Delete"
          danger
          onClose={() => setDeletingRun(null)}
          onConfirm={() => deleteRun.mutate(deletingRun.id)}
          isSaving={deleteRun.isPending}
        />
      )}

      {reversingRun && (
        <ConfirmModal
          title="Reverse Payroll Run"
          message={`Return the ${fmtPeriod(reversingRun.periodMonth, reversingRun.periodYear)} payroll run to Draft status? The run will be unlocked for editing or deletion. Note: if salaries were already paid out, this is an accounting correction only — no money is recalled automatically.`}
          confirmLabel="Reverse to Draft"
          danger
          onClose={() => setReversingRun(null)}
          onConfirm={() => reverseRun.mutate(reversingRun.id)}
          isSaving={reverseRun.isPending}
        />
      )}
    </div>
  );
}
